import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { TopChrome } from '../components/TopChrome';
import { useTokens } from '../state/theme';
import { MasteryMappa, type MasteryMappaItem } from '../components/MasteryMappa';
import { studentProgressoApi } from '../api/student-progresso';
import { progressApi } from '../api/progress';
import { lezioneApi } from '../api/lezione';

type ProgressSource<T> =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'ready'; data: T };

function useProgressSource<T>(fetchData: () => Promise<T>) {
  const [state, setState] = useState<ProgressSource<T>>({ kind: 'loading' });
  const requestEpoch = useRef(0);
  const pending = useRef<number | null>(null);
  const reload = useCallback(() => {
    if (pending.current !== null) return;
    const epoch = ++requestEpoch.current;
    pending.current = epoch;
    setState({ kind: 'loading' });
    void Promise.resolve().then(fetchData).then(
      (data) => {
        if (requestEpoch.current === epoch) setState({ kind: 'ready', data });
      },
      () => {
        if (requestEpoch.current === epoch) setState({ kind: 'unavailable' });
      },
    ).finally(() => {
      if (requestEpoch.current === epoch) pending.current = null;
    });
  }, [fetchData]);

  useEffect(() => {
    reload();
    return () => {
      // Retire old callbacks on unmount and StrictMode effect replay.
      requestEpoch.current += 1;
      pending.current = null;
    };
  }, [reload]);
  return [state, reload] as const;
}

export default function MioProgressoScreen(): JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();
  const [phase, reloadMastery] = useProgressSource(studentProgressoApi.fetch);
  const [progressState, reloadProgress] = useProgressSource(progressApi.summary);
  const [lessonState, reloadLessons] = useProgressSource(lezioneApi.lessonProgressSummary);
  const hasUnavailable = [phase, progressState, lessonState].some((source) => source.kind === 'unavailable');

  function retryUnavailable() {
    if (phase.kind === 'unavailable') reloadMastery();
    if (progressState.kind === 'unavailable') reloadProgress();
    if (lessonState.kind === 'unavailable') reloadLessons();
  }

  const retryControl = hasUnavailable ? (
    <div role="status" style={{ fontFamily: tokens.fonts.body, color: tokens.textMuted, fontSize: 14 }}>
      {phase.kind === 'ready' && <p style={{ margin: '0 0 10px' }}>Alcuni dati non sono disponibili.</p>}
      <button
        type="button"
        onClick={retryUnavailable}
        style={{ background: tokens.accent, color: '#fff', border: 'none', borderRadius: tokens.radius.pill, padding: '10px 18px', font: 'inherit', fontWeight: 600, cursor: 'pointer' }}
      >
        Riprova
      </button>
    </div>
  ) : null;

  return (
    <Screen>
      <TopChrome
        title="Progressi"
        subtitle="Preparazione patente B"
      />

      <main
        data-testid="mio-progresso-screen"
        style={{
          padding: '24px 16px',
          overflow: 'auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {phase.kind === 'loading' && (
          /* V29 S5 (audit A6): skeleton shaped like the real cockpit — hero
             card + map card — instead of bare text. */
          <div data-testid="mio-progresso-loading" aria-busy="true" aria-label="Sto caricando i tuoi progressi" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ height: 130, borderRadius: tokens.radius.lg, background: tokens.line, animation: 'asc-skel 1.4s ease-in-out infinite' }} />
            <div style={{ height: 260, borderRadius: tokens.radius.md, background: tokens.line, animation: 'asc-skel 1.4s ease-in-out infinite' }} />
          </div>
        )}

        {phase.kind === 'loading' && retryControl}

        {phase.kind === 'unavailable' && (
          <div data-testid="mio-progresso-error" role="alert" style={{ color: tokens.wrong, fontSize: 14 }}>
            Non sono riuscito a caricare i tuoi progressi. Riprova tra un attimo.
            <div style={{ marginTop: 12 }}>{retryControl}</div>
          </div>
        )}

        {phase.kind === 'ready' && (() => {
          const { currentIndice, perArgomentoMastery } = phase.data;
          const progress = progressState.kind === 'ready' ? progressState.data : null;
          const lessonProgress = lessonState.kind === 'ready' ? lessonState.data : null;
          const isFresh = !perArgomentoMastery.some((arg) => arg.principles_observed > 0);
          const streakDays = progress?.currentStreakDays;
          const completionTotals = lessonProgress?.totals ?? null;
          const indice = currentIndice.indice;
          // Only a successful response can establish a real zero.
          const simulaCount = progress?.simulaCount;
          const simulaAvgScore = progress?.simulaAvgScore ?? null;

          const mappaItems: MasteryMappaItem[] = perArgomentoMastery.map((arg) => ({
            argomentoId: arg.argomento_id,
            order: arg.sort_order,
            nameIt: arg.name_it,
            averageMastery: arg.average_mastery,
            principlesTotal: arg.principles_total,
            principlesObserved: arg.principles_observed,
          }));

          return (
            <>
              <h1
                style={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  padding: 0,
                  margin: -1,
                  overflow: 'hidden',
                  clip: 'rect(0, 0, 0, 0)',
                  border: 0,
                }}
              >
                Progressi
              </h1>

              <section
                data-testid="mio-progresso-livello"
                aria-label={`${isFresh
                  ? 'Indice di preparazione: nessuna evidenza dalle risposte.'
                  : `Indice di preparazione: indice ${indice} su 100.`} ${completionTotals
                  ? `Percorso completato: ${completionTotals.completedLessons} su ${completionTotals.totalLessons} lezioni, ${completionTotals.completionPct}%.`
                  : lessonState.kind === 'loading' ? 'Percorso completato: caricamento in corso.' : 'Percorso completato: non disponibile.'} ${progress
                  ? (progress.simulaCount > 0
                    ? `Simulazioni d'esame completate: ${progress.simulaCount}${simulaAvgScore != null ? `, media ${simulaAvgScore} su 30` : ''}.`
                    : "Simulazioni d'esame: nessuna ancora.")
                  : progressState.kind === 'loading' ? "Simulazioni d'esame: caricamento in corso." : "Simulazioni d'esame: non disponibili."}`}
                style={{
                  padding: '18px 18px 16px',
                  background: tokens.brandGradient,
                  borderRadius: tokens.radius.lg,
                  fontFamily: tokens.fonts.body,
                  color: '#fff',
                }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      color: 'rgba(255,255,255,0.85)',
                    }}
                  >
                    Il tuo progresso
                  </div>
                  <span
                    data-testid="mio-progresso-streak-count"
                    aria-label={progress ? `Giorni consecutivi: ${streakDays}.` : `Giorni consecutivi: ${progressState.kind === 'loading' ? 'caricamento in corso' : 'non disponibili'}.`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      fontWeight: 700,
                      fontSize: 13.5,
                      color: '#fff',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" width="15" height="15" aria-hidden="true">
                      <path d="M12 2c1 3-1 4-1 6a3 3 0 003 3c1-1 1-3 0-4 2 1 4 4 4 7a6 6 0 01-12 0c0-4 4-6 6-12z" />
                    </svg>
                    {progress ? `${streakDays} ${streakDays === 1 ? 'giorno' : 'giorni'}` : progressState.kind === 'loading' ? 'Serie in caricamento…' : 'Serie non disponibile'}
                  </span>
                </div>

                <hr
                  aria-hidden="true"
                  style={{
                    border: 'none',
                    height: 1,
                    background: 'rgba(255,255,255,0.22)',
                    margin: '12px 0',
                  }}
                />

                {/* Sim-KPI round (founder-locked Option A, 2026-07-13): a 2x2
                    grid that reads as a story — top row is what you DID
                    (lessons, exam simulations), bottom row is where you STAND
                    (path %, readiness index). The simulations tile makes the
                    simulation-to-Progressi link explicit at the top. */}
                <div
                  data-testid="mio-progresso-stats"
                  style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', textAlign: 'center' }}
                >
                  {completionTotals ? (
                    <HeroStat figure={`${completionTotals.completedLessons} su ${completionTotals.totalLessons}`} label="lezioni completate" cellPad />
                  ) : (
                    <HeroStat figure="—" label={lessonState.kind === 'loading' ? 'lezioni: caricamento…' : 'lezioni: non disponibili'} cellPad />
                  )}
                  <HeroStat
                    testId="mio-progresso-simula-stat"
                    figure={progress ? String(simulaCount) : '—'}
                    label={progress ? (simulaCount === 1 ? "simulazione d'esame" : "simulazioni d'esame") : progressState.kind === 'loading' ? 'simulazioni: caricamento…' : 'simulazioni: non disponibili'}
                    sub={simulaAvgScore != null ? `media ${simulaAvgScore} su 30` : undefined}
                    borderLeft
                    cellPad
                  />
                  {completionTotals ? (
                    <HeroStat figure={`${completionTotals.completionPct}%`} label="percorso completato" borderTop cellPad />
                  ) : (
                    <HeroStat figure="—" label={lessonState.kind === 'loading' ? 'percorso completato: caricamento…' : 'percorso completato: non disponibile'} borderTop cellPad />
                  )}
                  <HeroStat
                    testId="mio-progresso-indice-stat"
                    figure={String(indice)}
                    suffix="/100"
                    label="indice di preparazione"
                    borderLeft
                    borderTop
                    emphasis
                    cellPad
                  />
                </div>
              </section>

              {retryControl}

              {/* V29 S5: the cold start finally SAYS it's a starting line —
                  the V28 'Iniziamo a colorare la mappa' state (was only an
                  aria-label difference; audit finding). */}
              {isFresh && completionTotals?.completedLessons === 0 && (
                <p
                  data-testid="mio-progresso-coldstart"
                  style={{
                    margin: 0,
                    padding: '10px 14px',
                    background: tokens.accentSoft,
                    borderRadius: tokens.radius.md,
                    fontFamily: tokens.fonts.body,
                    fontSize: 13.5,
                    lineHeight: 1.5,
                    color: tokens.text,
                  }}
                >
                  <b>Iniziamo a colorare la mappa.</b> Completa la prima lezione
                  e questa pagina prende vita: ogni argomento si colora con la
                  tua preparazione reale.
                </p>
              )}

              <section
                data-testid="mio-progresso-argomenti"
                aria-label="Argomenti d'esame"
                style={{
                  padding: '16px 16px 18px',
                  background: tokens.surface,
                  border: `1px solid ${tokens.line}`,
                  borderRadius: tokens.radius.md,
                  fontFamily: tokens.fonts.body,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
                  <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: tokens.text }}>
                    Argomenti d'esame
                  </h2>
                  <span style={{ marginLeft: 'auto', fontSize: 12.5, color: tokens.textMuted }}>
                    {mappaItems.length} {mappaItems.length === 1 ? 'argomento' : 'argomenti'}
                  </span>
                </div>
                <MasteryMappa
                  items={mappaItems}
                  onSelect={(argomentoId) => navigate(`/studia/argomento/${argomentoId}`)}
                />
              </section>
            </>
          );
        })()}

      </main>
    </Screen>
  );
}

function HeroStat({
  figure,
  suffix,
  label,
  sub,
  borderLeft = false,
  borderTop = false,
  cellPad = false,
  emphasis = false,
  testId,
}: {
  figure: string;
  suffix?: string;
  label: string;
  /** Small bold line under the label (e.g. "media 24 su 30"). */
  sub?: string;
  borderLeft?: boolean;
  borderTop?: boolean;
  /** Grid-cell padding for the 2x2 layout. */
  cellPad?: boolean;
  emphasis?: boolean;
  testId?: string;
}): JSX.Element {
  return (
    <div
      data-testid={testId}
      style={{
        minWidth: 0,
        padding: cellPad ? '8px 6px' : undefined,
        paddingLeft: cellPad ? 10 : (borderLeft ? 10 : 0),
        borderLeft: borderLeft
          ? `1px solid rgba(255,255,255,${emphasis ? 0.35 : 0.25})`
          : 'none',
        borderTop: borderTop ? '1px solid rgba(255,255,255,0.22)' : 'none',
      }}
    >
      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>
        {figure}
        {suffix && (
          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>
            {suffix}
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: '0.68rem',
          marginTop: 3,
          color: emphasis ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.8)',
          fontWeight: emphasis ? 700 : 400,
        }}
      >
        {label}
      </div>
      {sub && (
        <div style={{ fontSize: '0.72rem', marginTop: 2, color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
          {sub}
        </div>
      )}
    </div>
  );
}
